import React, { Fragment, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";

import { filterCatalog } from "../utils/exercise-catalog";
import SelectedExercises from "../views/coaching-portal/exercise-library/components/SelectedExercises";
import CatalogList from "../views/coaching-portal/exercise-library/components/CatalogList";
import CatalogSelectors from "../views/coaching-portal/exercise-library/components/CatalogSelectors";
import PreviewBranding from "./dashboard/settings/branding/PreviewBranding";

import LoginWrapper from "./LoginWrapper";
import OnboardingHeader from "./onboarding/OnboardingHeader";
import BuildApp from "./onboarding/BuildApp";
import { useRillContext } from "../rill/RillReact/share/base";

function OnboardingExerciseLibraryView(props) {
  const { t } = useTranslation("auth");
  const { locals } = useRillContext();

  const { activeUser = {}, exerciseCatalog } = locals;
  const [step, setStep] = useState("exerciseSelection");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("Biceps");
  const initialState = {
    squat: [],
    bench: [],
    deadlift: [],
    assistant: [],
    clean: [],
    jerk: [],
    snatch: [],
    overheadPress: [],
    other: [],
  };
  const [selectedExercises, setSelectedExercises] = useState(initialState);
  const [screenSize, setScreenSize] = useState({ width: null, height: null });

  const filteredExercises = useMemo(() => {
    return filterCatalog(exerciseCatalog, filter, search);
  }, [exerciseCatalog, filter, search]);

  const handleSelect = (exercise) => {
    const { primary } = exercise;
    setSelectedExercises((prevState) => {
      return { ...prevState, [primary]: [...prevState[primary], exercise] };
    });
  };

  const handleDeselect = (exercise) => {
    const { _id, primary } = exercise;
    setSelectedExercises((prevState) => {
      return {
        ...prevState,
        [primary]: prevState[primary].filter((ex) => ex._id !== _id),
      };
    });
  };

  const deselectAll = () => {
    setSelectedExercises(initialState);
  };

  const handleBuildApp = (e) => {
    e && e.preventDefault();
    submit("/actions/user/catalog-add-exercises", {
      method: "POST",
      body: { selectedExercises, onboarding: true },
    });
  };

  const handleReturnButton = () => {
    switch (step) {
      case "exerciseSelection":
        return;
      case "buildApp":
        setStep("exerciseSelection");
      default:
        break;
    }
  };

  const playAnimation = (e) => {
    e.preventDefault();
    setStep("buildAnimation");
    setTimeout(() => {
      handleBuildApp();
    }, 4000);
  };

  const updateDimensions = () => {
    setScreenSize({ width: window.innerWidth, height: window.innerHeight });
  };

  useEffect(() => {
    setScreenSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", updateDimensions);
    return () => {
      return window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  const {
    squat,
    bench,
    deadlift,
    assistant,
    clean,
    jerk,
    snatch,
    overheadPress,
    other,
  } = selectedExercises;
  const invalid =
    !squat.length &&
    !bench.length &&
    !deadlift.length &&
    !assistant.length &&
    !clean.length &&
    !jerk.length &&
    !snatch.length &&
    !overheadPress.length &&
    !other.length;
  const { businessName, customBranding = {} } = activeUser;
  const appText = businessName || "Your";
  if (step === "buildAnimation")
    return <BuildApp customBranding={customBranding} />;
  return (
    <div id="coaching-wrapper">
      <LoginWrapper
        mainClass="Onboarding exercise-catalog"
        left={
          <>
            {step === "exerciseSelection" ? (
              <Fragment>
                <OnboardingHeader
                  onBack={handleReturnButton}
                  active={4}
                  phrase="Exercise"
                  title="Exercise Library"
                  noBack
                />

                <article>
                  <CatalogSelectors
                    onSelectorClick={setFilter}
                    filter={filter}
                    mobile={screenSize && screenSize.width <= 960}
                    selectedExercises={selectedExercises}
                    search={search}
                    onChangeFn={(e) => setSearch(e.target.value)}
                  />

                  <CatalogList
                    filter={filter}
                    filteredExercises={filteredExercises}
                    selectedExercises={selectedExercises}
                    handleDeselect={handleDeselect}
                    handleSelect={handleSelect}
                    mobile={screenSize && screenSize.width <= 960}
                    search={search}
                    onChangeFn={(e) => setSearch(e.target.value)}
                  />
                </article>

                <footer>
                  <button
                    data-button-type="primary"
                    onClick={(e) => {
                      e.preventDefault();
                      setStep("buildApp");
                    }}
                    type="button"
                    disabled={invalid}
                    sz="large"
                  >
                    {t("onboardingExerciseLibrary.continue")}
                  </button>
                </footer>
              </Fragment>
            ) : (
              <>
                <div className="self-start mobile-hidden">
                  <h1 className="text-5xl!">
                    {t("onboardingExerciseLibrary.trainingApp", {
                      name: appText,
                    })}
                  </h1>
                  <p className="mt-4">
                    {t("onboardingExerciseLibrary.buildAppPrompt")}
                  </p>

                  <button
                    data-button-type="primary"
                    onClick={playAnimation}
                    type="button"
                    sz="large"
                    className="mt-16 w-2/3 justify-center"
                  >
                    {t("onboardingExerciseLibrary.buildApp")}
                  </button>
                </div>
                <div className="only-show-mobile app-preview">
                  <div className="mobile-branding-placeholder">
                    <PreviewBranding customBranding={customBranding} mobile />
                  </div>
                  <div>
                    <h1>
                      {t("onboardingExerciseLibrary.trainingApp", {
                        name: appText,
                      })}
                    </h1>
                    <span>
                      {t("onboardingExerciseLibrary.buildAppPrompt")}
                    </span>
                  </div>

                  <footer>
                    <button
                      data-button-type="primary"
                      onClick={playAnimation}
                      type="button"
                      sz="large"
                    >
                      {t("onboardingExerciseLibrary.continue")}
                      </button>
                  </footer>
                </div>
              </>
            )}
          </>
        }
        right={
          step === "exerciseSelection" ? (
            <SelectedExercises
              selectedExercises={selectedExercises}
              isOnboarding
              handleDeselect={handleDeselect}
              deselectAll={deselectAll}
            />
          ) : screenSize && screenSize.width > 960 ? (
            <PreviewBranding customBranding={customBranding} />
          ) : null
        }
      />
    </div>
  );
}

const OnboardingExerciseLibrary = (app) => {
  app.get(
    "/onboarding-exercise-library",
    async ({ res, isLoggedIn, flash, load }) => {
      if (!isLoggedIn()) {
        flash("error", "Please login first");
        return res.redirect("/login");
      }
      await load("exerciseCatalog", {});

      return (res.body = <OnboardingExerciseLibraryView />);
    }
  );
};

export default OnboardingExerciseLibrary;
